import type { DateRange } from "@daypicker/react"
import * as React from "react"
import { trackActivity, trackDatesSubmitted } from "../activity"
import { CALENDAR_FRESH_MS, dayDifference, isRequiredHireDay, MINIMUM_HIRE_MESSAGE } from "../dateSelection"
import { toDateOnly } from "../format"
import type { useBrowserSession } from "./useBrowserSession"
import type { useCalendarAvailability } from "./useCalendarAvailability"
import type { useCheckoutAnalytics } from "./useCheckoutAnalytics"
import type { useCheckoutLifecycle } from "./useCheckoutLifecycle"
import type { useCheckoutModel } from "./useCheckoutModel"
import type { useCheckoutState } from "./useCheckoutState"

type Inputs = Pick<ReturnType<typeof useCheckoutState>,
    "setActiveDateField" | "isMobile" | "setCalendarOpen" | "calendarPhase" | "calendarSnapshot" | "activeSlug" | "quantity" | "calendarController" | "setRange" | "setAvailability" | "setQuote" | "setQuantity" | "setError" | "dateNoticeTimer" | "setDateNotice" | "product" | "range" | "activeDateField" | "postcode">
    & Pick<ReturnType<typeof useCheckoutModel>,
        "normalizedPostcode" | "stockCap" | "calendarReady" | "selection" | "deliveryContactRequired">
    & Pick<ReturnType<typeof useCalendarAvailability>,
        "refreshCalendar">
    & Pick<ReturnType<typeof useCheckoutLifecycle>,
        "cancelAvailabilityCheck" | "invalidateCheckout">
    & Pick<ReturnType<typeof useCheckoutAnalytics>,
        "analytics">
    & Pick<ReturnType<typeof useBrowserSession>,
        "browserSessionId">

export function useBookingSelection(inputs: Inputs) {
    const {
        setActiveDateField,
        isMobile,
        setCalendarOpen,
        calendarPhase,
        calendarSnapshot,
        activeSlug,
        quantity,
        normalizedPostcode,
        deliveryContactRequired,
        calendarController,
        refreshCalendar,
        cancelAvailabilityCheck,
        invalidateCheckout,
        setRange,
        setAvailability,
        setQuote,
        setQuantity,
        setError,
        dateNoticeTimer,
        setDateNotice,
        product,
        stockCap,
        range,
        calendarReady,
        activeDateField,
        analytics,
        selection,
        postcode,
        browserSessionId,
    } = inputs
    const openDatePicker = (field: "start" | "end") => {
        if (deliveryContactRequired) return
        if (!normalizedPostcode) {
            showDateNotice("Please enter your delivery postcode to check availability for your area.", 5000, "postcode")
            return
        }
        setActiveDateField(field)
        if (isMobile) setCalendarOpen(true)
        const fresh = calendarPhase === "ready" && calendarSnapshot?.productSlug === activeSlug
            && calendarSnapshot.quantity === quantity && calendarSnapshot.postcode === normalizedPostcode
            && Date.now() - calendarSnapshot.loadedAt < CALENDAR_FRESH_MS
            && Date.now() < Date.parse(calendarSnapshot.noticeValidUntil)
        if (!fresh && !calendarController.current) void refreshCalendar()
    }

    const clearBookingResult = React.useCallback(() => {
        cancelAvailabilityCheck()
        invalidateCheckout()
        setAvailability(null)
        setQuote(null)
        setError("")
        if (dateNoticeTimer.current !== null) window.clearTimeout(dateNoticeTimer.current)
        setDateNotice(null)
    }, [cancelAvailabilityCheck, invalidateCheckout])

    const resetDates = () => {
        setRange(undefined)
        setActiveDateField("start")
        clearBookingResult()
    }

    const changeQuantity = (next: number) => {
        setQuantity(Math.max(1, Math.min(stockCap, next)))
        clearBookingResult()
    }

    const showDateNotice = (message: string, duration = 2800, target: "calendar" | "postcode" = "calendar") => {
        if (dateNoticeTimer.current !== null) window.clearTimeout(dateNoticeTimer.current)
        setDateNotice({ id: Date.now(), message, target })
        dateNoticeTimer.current = window.setTimeout(() => setDateNotice(null), duration)
    }

    const isSelectedStartDate = (day: Date) => Boolean(range?.from && dayDifference(range.from, day) === 0)

    const chooseDay = (day: Date, modifiers: Record<string, boolean>) => {
        if (isSelectedStartDate(day)) { resetDates(); return }
        if (!calendarReady) return
        if (modifiers.ahPast) { showDateNotice("Your start date is in the past."); return }
        if (modifiers.ahNoticeCutoff) {
            showDateNotice(normalizedPostcode ? "That delivery date is unavailable for your postcode or booking time." : "Enter your delivery postcode to check local same-day and next-day booking.")
            return
        }
        if (activeDateField === "end" && isRequiredHireDay(day, range?.from, product)) {
            showDateNotice(MINIMUM_HIRE_MESSAGE)
            return
        }
        if (modifiers.disabled) return
        const picked = new Date(day.getFullYear(), day.getMonth(), day.getDate())
        let next: DateRange
        if (activeDateField === "start" || !range?.from) {
            next = { from: picked, to: undefined }
            setActiveDateField("end")
        } else {
            next = picked < range.from ? { from: picked, to: range.from } : { from: range.from, to: picked }
        }
        if (next.from && !next.to) trackActivity("dates_started", { delivery_date: toDateOnly(next.from) })
        setRange(next)
        clearBookingResult()
        if (next.from && next.to) {
            analytics("hire_dates_selected", { delivery_date: toDateOnly(next.from), collection_date: toDateOnly(next.to) })
            // Mobile date edits are provisional until the calendar's Select button.
            if (!isMobile) trackDatesSubmitted()
        }
    }

    const confirmDateSelection = () => {
        if (!isMobile || !calendarReady || !selection) return
        trackDatesSubmitted()
        setCalendarOpen(false)
    }

    const commonRequest = React.useMemo(() => ({ productSlug: product?.slug, selection, quantity, postcode, browserSessionId }), [browserSessionId, postcode, product?.slug, quantity, selection])

    return {
        openDatePicker,
        clearBookingResult,
        resetDates,
        changeQuantity,
        isSelectedStartDate,
        chooseDay,
        confirmDateSelection,
        commonRequest,
    }
}
